import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

const PerfilPublico = () => {
    const { codigo } = useParams();
    const [perfil, setPerfil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/nfc/${codigo}`)
            .then(({ data }) => setPerfil(data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [codigo]);

    // Asegura que una URL tenga https://
    const fixUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return 'https://' + url;
    };

    const handleVCard = () => {
        if (!perfil) return;
        const vcard = [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${perfil.nombre} ${perfil.apellido}`,
            `N:${perfil.apellido};${perfil.nombre};;;`,
            perfil.cargo    ? `TITLE:${perfil.cargo}`            : '',
            perfil.empresa  ? `ORG:${perfil.empresa}`            : '',
            perfil.telefono ? `TEL;TYPE=VOICE:${perfil.telefono}` : '',
            perfil.whatsapp ? `TEL;TYPE=CELL:${perfil.whatsapp}`  : '',
            perfil.email    ? `EMAIL:${perfil.email}`            : '',
            perfil.website  ? `URL:${fixUrl(perfil.website)}`    : '',
            'END:VCARD'
        ].filter(Boolean).join('\n');

        const blob = new Blob([vcard], { type: 'text/vcard' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `${perfil.nombre}_${perfil.apellido}.vcf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-gray-400 text-sm">Cargando...</div>
        </div>
    );

    if (notFound) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-3 px-4">
            <div className="text-5xl">💳</div>
            <h1 className="text-xl font-semibold text-gray-700">Perfil no encontrado</h1>
            <p className="text-gray-400 text-sm text-center">Esta tarjeta no está activa o no existe.</p>
        </div>
    );

    const youtubeId = perfil.youtube
        ? perfil.youtube.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1]
        : null;

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-600 to-blue-800 flex flex-col items-center">
            {/* Hero */}
            <div className="w-full max-w-sm px-4 pt-10 pb-6 flex flex-col items-center text-center">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-xl mb-4">
                    {perfil.foto_url ? (
                        <img src={perfil.foto_url} alt="Foto" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full bg-blue-300 flex items-center justify-center text-4xl text-white">
                            {perfil.nombre?.charAt(0) || '?'}
                        </div>
                    )}
                </div>
                <h1 className="text-2xl font-bold text-white">
                    {perfil.nombre} {perfil.apellido}
                </h1>
                {perfil.cargo && <p className="text-blue-100 text-sm mt-1">{perfil.cargo}</p>}
                {perfil.empresa && <p className="text-blue-200 text-xs mt-0.5">{perfil.empresa}</p>}
            </div>

            {/* Card principal */}
            <div className="w-full max-w-sm bg-white rounded-t-3xl flex-1 px-5 pt-6 pb-10 shadow-2xl">

                {/* Botones de acción rápida */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                    {perfil.telefono && (
                        <a href={`tel:${perfil.telefono}`}
                           className="flex flex-col items-center gap-1 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition">
                            <span className="text-2xl">📞</span>
                            <span className="text-xs text-gray-500">Llamar</span>
                        </a>
                    )}
                    {perfil.whatsapp && (
                        <a href={`https://wa.me/${perfil.whatsapp.replace(/\D/g, '')}`}
                           target="_blank" rel="noreferrer"
                           className="flex flex-col items-center gap-1 p-3 bg-gray-50 rounded-xl hover:bg-green-50 transition">
                            <span className="text-2xl">💬</span>
                            <span className="text-xs text-gray-500">WhatsApp</span>
                        </a>
                    )}
                    {perfil.email && (
                        <a href={`mailto:${perfil.email}`}
                           className="flex flex-col items-center gap-1 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition">
                            <span className="text-2xl">✉️</span>
                            <span className="text-xs text-gray-500">E-mail</span>
                        </a>
                    )}
                </div>

                {/* Redes sociales */}
                {(perfil.linkedin || perfil.instagram || perfil.youtube || perfil.website) && (
                    <div className="flex justify-center gap-3 mb-6">
                        {perfil.linkedin && (
                            <a href={fixUrl(perfil.linkedin)} target="_blank" rel="noreferrer"
                               className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white text-sm hover:opacity-80">
                                in
                            </a>
                        )}
                        {perfil.instagram && (
                            <a href={fixUrl(perfil.instagram)} target="_blank" rel="noreferrer"
                               className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center text-white text-sm hover:opacity-80">
                                ig
                            </a>
                        )}
                        {perfil.youtube && (
                            <a href={fixUrl(perfil.youtube)} target="_blank" rel="noreferrer"
                               className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white text-sm hover:opacity-80">
                                yt
                            </a>
                        )}
                        {perfil.website && (
                            <a href={fixUrl(perfil.website)} target="_blank" rel="noreferrer"
                               className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm hover:opacity-80">
                                🌐
                            </a>
                        )}
                    </div>
                )}

                {/* Bio */}
                {perfil.bio && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Acerca de mí</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{perfil.bio}</p>
                    </div>
                )}

                {/* Video YouTube */}
                {youtubeId && (
                    <div className="mb-6 rounded-xl overflow-hidden">
                        <iframe className="w-full" height="200"
                            src={`https://www.youtube.com/embed/${youtubeId}`}
                            title="Video" frameBorder="0" allowFullScreen />
                    </div>
                )}

                {/* Botones secundarios */}
                <div className="space-y-3">
                    {perfil.website && (
                        <a href={fixUrl(perfil.website)} target="_blank" rel="noreferrer"
                           className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition">
                            🌐 Visitar sitio web
                        </a>
                    )}
                    <button onClick={handleVCard}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white text-sm font-medium transition"
                        style={{ backgroundColor: '#0057B7' }}>
                        👤 Guardar contacto
                    </button>
                </div>

                <div className="mt-8 text-center">
                    <p className="text-xs text-gray-300">Powered by <span className="font-semibold text-gray-400">Time Telco</span></p>
                </div>
            </div>
        </div>
    );
};

export default PerfilPublico;
